import { useEffect, useState } from 'react';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VaultData } from '../hooks/useVaultData';
import { buildClaimRefShare, fmtUnits9 } from '../ton/vault';
import {
  buildClaimRef,
  fetchSandJettons,
  fetchSandRefClaims,
  type SandJettonMeta,
  type SandRefClaim,
} from '../ton/sand';
import { sanitizeReferrerKey } from '../ton/referral';
import { gaEvent } from '../analytics';
import { resolveOutgoingReferrerKey } from '../ton/notify';

interface RewardsCardProps {
  data: VaultData;
  /** The wallet already holds an on-chain key (usePyramidaState `me.registered`). */
  registered: boolean;
}

/** Small amounts keep their significant digits (0.00425), big ones round. */
const fmtAmount = (raw: bigint, decimals: number) => {
  const v = Number(raw) / 10 ** decimals;
  if (v === 0) return '0';
  if (v >= 1000) return v.toLocaleString('en', { maximumFractionDigits: 0 });
  if (v >= 1) return v.toLocaleString('en', { maximumFractionDigits: 2 });
  return v.toLocaleString('en', { maximumFractionDigits: 6 });
};

/**
 * Claimable earnings, all in one place: the wallet's accumulated referral
 * balance on the mini-games bank (ClaimRefShare) plus every pending Sand
 * lottery referral claim (ClaimRef per token).
 */
export function RewardsCard({ data, registered }: RewardsCardProps) {
  const { t } = useTranslation();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const { refBalance, refresh } = data;
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Sand lottery referral claims of the connected wallet, plus token
  // metadata to label them. Every slot is rendered even when the metadata
  // lookup fails — the amount falls back to a raw value with '???'.
  const [sandClaims, setSandClaims] = useState<SandRefClaim[]>([]);
  const [sandMeta, setSandMeta] = useState<Map<string, SandJettonMeta>>(new Map());
  const [claiming, setClaiming] = useState<string | null>(null);
  const [refKey, setRefKey] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const key = sanitizeReferrerKey(
        await resolveOutgoingReferrerKey(address || null),
      );
      if (key !== null && !cancelled) setRefKey(key);
    })();
    return () => {
      cancelled = true;
    };
  }, [address]);

  useEffect(() => {
    if (!address) {
      setSandClaims([]);
      return;
    }
    let cancelled = false;
    void fetchSandRefClaims(address).then((list) => {
      if (!cancelled) setSandClaims(list);
    });
    void fetchSandJettons().then((map) => {
      if (!cancelled) setSandMeta(map);
    });
    return () => {
      cancelled = true;
    };
  }, [address]);

  // Re-read the on-chain claim list once the claim transaction had time to
  // settle: a short gas budget pays only the first few tokens, and the
  // remainder must stay visible instead of looking claimed.
  const refreshSandClaims = (addr: string) => {
    setTimeout(() => {
      void fetchSandRefClaims(addr).then((list) => setSandClaims(list));
    }, 15000);
  };

  const claim = async () => {
    if (!address) {
      void tonConnectUI.openModal();
      return;
    }
    if (refBalance <= 0n || pending) return;
    setPending(true);
    setNotice(null);
    try {
      await tonConnectUI.sendTransaction(buildClaimRefShare());
      gaEvent('claim_rewards', { source: 'vault' });
      setNotice(t('v2.rewards.sent'));
      setTimeout(refresh, 8000);
    } catch {
      setNotice(t('v2.rewards.cancelled'));
    } finally {
      setPending(false);
    }
  };

  // One ClaimRef settles the whole pending list on-chain: the requested
  // token goes out first, the excess loop then pays every other pending
  // jetton claim, and the finish refunds the change plus the GRAM claim.
  // Target the first jetton claim when there is one (its chain sweeps the
  // GRAM balance too); a GRAM-only claim goes straight to the GRAM path.
  //
  // The invite key rides along ONLY for a still-unregistered wallet: the
  // contract reserves the whole registrar-resolve value out of the attached
  // claim gas when a key is present, which starves the excess loop and
  // degrades the claim to a single fixed-value transfer of one token. A
  // registered wallet's link is set-once anyway — re-supplying gains nothing
  // and costs the cascade.
  const claimSand = async (token?: string) => {
    const target = token
      ? sandClaims.find((c) => c.token === token)
      : sandClaims.find((c) => !c.isTon) ?? sandClaims[0];
    if (!target) return;
    setClaiming(target.token);
    setNotice(null);
    try {
      await tonConnectUI.sendTransaction(
        buildClaimRef(target.token, registered ? null : refKey, sandClaims.length),
      );
      gaEvent('claim_rewards', { source: 'sand' });
      setSandClaims([]);
      if (address) refreshSandClaims(address);
      setNotice(t('v2.rewards.sent'));
    } catch {
      setNotice(t('v2.rewards.cancelled'));
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div className="token-info-card rewards-card">
      <h3 className="card-head">
        <Gift size={18} aria-hidden="true" /> {t('v2.rewards.title')}
      </h3>
      <div className="rewards-card__value">
        {fmtUnits9(refBalance, 4)} <span>GRAM</span>
      </div>
      <p className="hint" style={{ textAlign: 'left' }}>
        {refBalance > 0n ? t('v2.rewards.ready') : t('v2.rewards.empty')}
      </p>
      {notice && <p className="hint" style={{ textAlign: 'left' }}>{notice}</p>}
      <button
        type="button"
        className="swap-submit"
        disabled={pending || (!!address && refBalance <= 0n)}
        onClick={() => void claim()}
      >
        {!address
          ? t('v2.vault.connect')
          : pending
            ? t('v2.vault.pending')
            : t('v2.rewards.claim')}
      </button>
      {sandClaims.length > 0 && (
        <div className="rewards-card__sand">
          <h4>{t('sand.rewardsTitle')}</h4>
          {sandClaims.map((c) => {
            const meta = c.isTon ? null : sandMeta.get(c.token) ?? null;
            const symbol = c.isTon ? 'GRAM' : meta?.symbol ?? '???';
            const decimals = c.isTon ? 9 : meta?.decimals ?? 9;
            return (
              <div className="sand-reward" key={c.token}>
                <span className="sand-reward__amt">
                  {fmtAmount(c.amount, decimals)} <b>{symbol}</b>
                </span>
                <button
                  type="button"
                  className="sand-reward__pick"
                  // A lone GRAM claim below the claim gas is not worth its
                  // own send — let it keep accumulating instead.
                  disabled={
                    claiming !== null || (c.isTon && c.amount < 100_000_000n)
                  }
                  onClick={() => void claimSand(c.token)}
                >
                  {claiming === c.token ? t('v2.vault.pending') : t('sand.claim')}
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="sand-reward__btn"
            // A lone GRAM claim below the claim gas is not worth its own
            // send — let it keep accumulating instead.
            disabled={
              claiming !== null ||
              (sandClaims.every((c) => c.isTon) &&
                sandClaims.every((c) => c.amount < 100_000_000n))
            }
            onClick={() => void claimSand()}
          >
            {claiming ? t('v2.vault.pending') : t('sand.claimAll')}
          </button>
          <p className="sand-reward__hint">{t('sand.claimHint')}</p>
        </div>
      )}
    </div>
  );
}
